import { computed, unref, toRef } from "vue";
import type { MaybeRef } from "vue";
import type { NitroFetchOptions } from "nitropack";
import type { FetchError } from "ofetch";
import type {
  AdjacentSiblings,
  ContentItem,
  ContentItems,
  ExpandOption,
  ItemQueryOptions,
  QueryOptions,
  GlobalFetchOptions,
  UseItemQueryOptions,
  UseQueryOptions,
} from "../types";
import type { _AsyncData } from "#app/composables/asyncData";
import { $apiFetch, useApiFetch } from "#imports";

function buildExpandQuery<T> ( expand: ExpandOption<T> ) {
  let result;
  if ( expand === "all" ) {
    result = "expand=" + encodeURIComponent( "properties[$all]" ) + "&";
  } else if ( typeof expand === "string" ) {
    result = "expand=" + encodeURIComponent( `properties[${expand}]` ) + "&";
  } else if ( Array.isArray( expand ) ) {
    result = "expand=" + encodeURIComponent( `properties[${expand.join( "," )}]` ) + "&";
  }

  return result;
}

export function $contentDeliveryResource ( options?: Omit<GlobalFetchOptions, "lazy"> ) {
  function getById<T = Record<string, any>> ( id: string, expand?: ExpandOption<T> ): Promise<ContentItem<T>> {
    let url_ = "/umbraco/delivery/api/v2/content/item/{id}?";

    if ( id === undefined || id === null ) {
      throw new Error( "The parameter 'id' must be defined." );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( "" + id ) );

    if ( expand !== undefined && expand !== null ) {
      url_ += buildExpandQuery<T>( expand );
    }

    url_ = url_.replace( /[?&]$/, "" );

    const fetchOptions: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      ...options,
    };

    return $apiFetch<ContentItem<T>>( url_, fetchOptions );
  }

  function getByRoute<T = Record<string, any>> ( path: string, expand?: ExpandOption<T> ): Promise<ContentItem<T>> {
    let url_ = "/umbraco/delivery/api/v2/content/item/{path}?";

    if ( path === undefined || path === null ) {
      throw new Error( "The parameter 'path' must be defined." );
    }
    url_ = url_.replace( "{path}", encodeURIComponent( "" + path ) );

    if ( expand !== undefined && expand !== null ) {
      url_ += buildExpandQuery<T>( expand );
    }

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      ...options,
    };

    return $apiFetch<ContentItem<T>>( url_, options_ );
  }

  function getItemsById<T = Record<string, any>> ( ids: string[], expand?: ExpandOption<T> ): Promise<ContentItem<T>> {
    let url_ = "/umbraco/delivery/api/v2/content/item?";

    if ( ids === undefined || ids === null || ids.length === 0 ) {
      throw new Error( "The parameter 'ids' must be defined." );
    }

    url_ += ids.map( id => `id[]=${encodeURIComponent( id )}&` ).join( "" );

    if ( expand !== undefined && expand !== null ) {
      url_ += buildExpandQuery<T>( expand );
    }

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      ...options,
    };

    return $apiFetch<ContentItem<T>>( url_, options_ );
  }

  function getItemsFromQuery<T = Record<string, any>> ( queryOptions: ItemQueryOptions<T> ): Promise<ContentItems<T>> {
    let url_ = "/umbraco/delivery/api/v2/content?";

    if ( queryOptions === null || queryOptions === undefined ) {
      throw new Error( "The query options must be defined." );
    }

    const fetch = queryOptions.fetch;
    if ( fetch !== undefined && fetch !== null ) {
      url_ += "fetch=" + encodeURIComponent( fetch ) + "&";
    }

    const filter = queryOptions.filter;
    if ( filter !== undefined && filter !== null && Object.keys( filter ).length ) {
      url_ += Object.entries( filter ).map( ( [k, v] ) => `filter=${encodeURIComponent( `${k}:${v}` )}&` ).join( "" );
    }

    const sort = queryOptions.sort;
    if ( sort !== undefined && sort !== null ) {
      if ( Array.isArray( sort ) ) {
        url_ += sort.map( i => `sort=${encodeURIComponent( i )}&` ).join( "" );
      } else {
        url_ += `sort=${encodeURIComponent( sort )}&`;
      }
    }

    const skip = queryOptions.skip;
    if ( skip !== undefined && skip !== null ) {
      url_ += "skip=" + encodeURIComponent( skip ) + "&";
    }

    const take = queryOptions.take;
    if ( take !== undefined && take !== null ) {
      url_ += "take=" + encodeURIComponent( take ) + "&";
    }

    const expand = queryOptions.expand;
    if ( expand !== undefined && expand !== null ) {
      url_ += buildExpandQuery<T>( expand );
    }

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      ...options,
    };

    return $apiFetch<ContentItems<T>>( url_, options_ );
  }

  function getByTypeSingle<T = Record<string, any>> ( type: string, expand?: ExpandOption<T> ): Promise<ContentItem<T>> {
    let url_ = "/api/content/type/single?";

    if ( type === undefined || type === null ) {
      throw new Error( "The parameter 'type' must be defined." );
    }

    url_ += "contentType=" + encodeURIComponent( type ) + "&";

    if ( expand !== undefined && expand !== null ) {
      url_ += buildExpandQuery<T>( expand );
    }

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<ContentItem<T>>( url_, options_ );
  }

  function getSiblings<T = Record<string, any>> ( id: string ): Promise<ContentItem<T>> {
    let url_ = "/api/content/{id}/siblings";

    if ( id === undefined || id === null ) {
      throw new Error( "The parameter 'id' must be defined." );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( "" + id ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<ContentItem<T>>( url_, options_ );
  }

  function getAdjacentSiblings<T = Record<string, any>> ( id: string ): Promise<AdjacentSiblings<T>> {
    let url_ = "/api/content/{id}/siblings/adjacent";

    if ( id === undefined || id === null ) {
      throw new Error( "The parameter 'id' must be defined." );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( "" + id ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<AdjacentSiblings<T>>( url_, options_ );
  }

  function getChildren<T = Record<string, any>> ( idOrPath: string, options?: Omit<QueryOptions<T>, "fetch"> ): Promise<ContentItems<T>> {
    return getItemsFromQuery<T>( {
      fetch: `children:${idOrPath}`,
      ...options,
    } );
  }

  function getAncestors<T = Record<string, any>> ( idOrPath: string, options?: Omit<QueryOptions<T>, "fetch"> ): Promise<ContentItems<T>> {
    return getItemsFromQuery<T>( {
      fetch: `children:${idOrPath}`,
      ...options,
    } );
  }

  function getDescendants<T = Record<string, any>> ( idOrPath: string, options?: Omit<QueryOptions<T>, "fetch"> ): Promise<ContentItems<T>> {
    return getItemsFromQuery<T>( {
      fetch: `descendants:${idOrPath}`,
      ...options,
    } );
  }

  function getByType<T = Record<string, any>> ( type: string, options?: Omit<QueryOptions<T>, "filter"> ): Promise<ContentItems<T>> {
    return getItemsFromQuery<T>( {
      filter: {
        contentType: type,
      },
      ...options,
    } );
  }

  return {
    getById,
    getByRoute,
    getItemsById,
    getItemsFromQuery,
    getByTypeSingle,
    getSiblings,
    getAdjacentSiblings,
    getChildren,
    getAncestors,
    getDescendants,
    getByType,
  };
}

export function useContentDeliveryResource ( options?: GlobalFetchOptions ) {
  function getById<T = Record<string, any>> ( id: MaybeRef<string>, expand?: MaybeRef<ExpandOption<T>> ): Promise<_AsyncData<ContentItem<T> | null, FetchError<any> | null>> {
    const computedUrl = computed( () => {
      let url_ = "/umbraco/delivery/api/v2/content/item/{id}?";

      const idUnref = unref( id );
      if ( idUnref === undefined || idUnref === null ) {
        throw new Error( "The parameter 'id' must be defined." );
      }
      url_ = url_.replace( "{id}", encodeURIComponent( "" + idUnref ) );

      const expandUnref = unref( expand );
      if ( expandUnref !== undefined && expandUnref !== null ) {
        url_ += buildExpandQuery<T>( expandUnref );
      }

      url_ = url_.replace( /[?&]$/, "" );

      return url_;
    } );

    const options_: UseApiFetchOptions<ContentItem<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<ContentItem<T>>( computedUrl, options_ );
  }

  function getByRoute<T = Record<string, any>> ( path: MaybeRef<string>, expand?: MaybeRef<ExpandOption<T>> ): Promise<_AsyncData<ContentItem<T> | null, FetchError<any> | null>> {
    const computedUrl = computed( () => {
      let url_ = "/umbraco/delivery/api/v2/content/item/{path}?";

      const pathUnref = unref( path );
      if ( pathUnref === undefined || pathUnref === null ) {
        throw new Error( "The parameter 'path' must be defined." );
      }
      url_ = url_.replace( "{path}", encodeURIComponent( "" + pathUnref ) );

      const expandUnref = unref( expand );
      if ( expandUnref !== undefined && expandUnref !== null ) {
        url_ += buildExpandQuery<T>( expandUnref );
      }

      url_ = url_.replace( /[?&]$/, "" );

      return url_;
    } );

    const options_: UseApiFetchOptions<ContentItem<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<ContentItem<T>>( computedUrl, options_ );
  }

  function getItemsById<T = Record<string, any>> ( ids: MaybeRef<string[]>, expand?: MaybeRef<ExpandOption<T>> ): Promise<_AsyncData<ContentItem<T>[] | null, FetchError<any> | null>> {
    const computedUrl = computed( () => {
      let url_ = "/umbraco/delivery/api/v2/content/item?";

      const idsUnref = unref( ids );
      if ( idsUnref === undefined || idsUnref === null || idsUnref.length === 0 ) {
        throw new Error( "The parameter 'ids' must be defined." );
      }

      url_ += idsUnref.map( id => `id[]=${encodeURIComponent( id )}&` ).join( "" );

      const expandUnref = unref( expand );
      if ( expandUnref !== undefined && expandUnref !== null ) {
        url_ += buildExpandQuery<T>( expandUnref );
      }

      url_ = url_.replace( /[?&]$/, "" );

      return url_;
    } );

    const options_: UseApiFetchOptions<ContentItem<T>[]> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<ContentItem<T>[]>( computedUrl, options_ );
  }

  function getItemsFromQuery<T = Record<string, any>> ( queryOptions: UseItemQueryOptions<T> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    const computedUrl = computed( () => {
      let url_ = "/umbraco/delivery/api/v2/content?";

      if ( queryOptions === null || queryOptions === undefined ) {
        throw new Error( "The query options must be defined." );
      }

      const fetchUnref = unref( queryOptions.fetch );
      if ( fetchUnref !== undefined && fetchUnref !== null ) {
        url_ += "fetch=" + encodeURIComponent( fetchUnref ) + "&";
      }

      const filterUnref = unref( queryOptions.filter );
      if ( filterUnref !== undefined && filterUnref !== null && Object.keys( filterUnref ).length ) {
        url_ += Object.entries( filterUnref ).map( ( [k, v] ) => `filter=${encodeURIComponent( `${k}:${unref( v )}` )}&` ).join( "" );
      }

      const sortUnref = unref( queryOptions.sort );
      if ( sortUnref !== undefined && sortUnref !== null ) {
        if ( Array.isArray( sortUnref ) ) {
          url_ += sortUnref.map( i => `sort=${encodeURIComponent( i )}&` ).join( "" );
        } else {
          url_ += `sort=${encodeURIComponent( sortUnref )}&`;
        }
      }

      const skipUnref = unref( queryOptions.skip );
      if ( skipUnref !== undefined && skipUnref !== null ) {
        url_ += "skip=" + encodeURIComponent( skipUnref ) + "&";
      }

      const takeUnref = unref( queryOptions.take );
      if ( takeUnref !== undefined && takeUnref !== null ) {
        url_ += "take=" + encodeURIComponent( takeUnref ) + "&";
      }

      const expandUnref = unref( queryOptions.expand );
      if ( expandUnref !== undefined && expandUnref !== null ) {
        url_ += buildExpandQuery<T>( expandUnref );
      }

      url_ = url_.replace( /[?&]$/, "" );

      return url_;
    } );

    const options_: UseApiFetchOptions<ContentItems<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      watch: [computedUrl],
      ...options,
    };

    return useApiFetch<ContentItems<T>>( computedUrl, options_ );
  }

  function getByTypeSingle<T = Record<string, any>> ( type: MaybeRef<string>, expand?: ExpandOption<T> ): Promise<_AsyncData<ContentItem<T> | null, FetchError<any> | null>> {
    const computedUrl = computed( () => {
      let url_ = "/api/content/type/single?";

      if ( type === undefined || type === null ) {
        throw new Error( "The parameter 'type' must be defined." );
      }

      const typeUnref = unref( type );
      if ( typeUnref !== undefined && typeUnref !== null ) {
        url_ += "contentType=" + encodeURIComponent( typeUnref ) + "&";
      }

      const expandUnref = unref( expand );
      if ( expandUnref !== undefined && expandUnref !== null ) {
        url_ += buildExpandQuery<T>( expandUnref );
      }

      url_ = url_.replace( /[?&]$/, "" );

      return url_;
    } );

    const options_: UseApiFetchOptions<ContentItem<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      watch: [computedUrl],
    };

    return useApiFetch<ContentItem<T>>( computedUrl, options_ );
  }

  function getSiblings<T = Record<string, any>> ( id: MaybeRef<string> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    let url_ = "/api/content/{id}/siblings";

    const idUnref = unref( id );
    if ( idUnref === undefined || idUnref === null ) {
      throw new Error( "The parameter 'id' must be defined." );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( "" + idUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: UseApiFetchOptions<ContentItems<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<ContentItems<T>>( url_, options_ );
  }

  function getAdjacentSiblings<T = Record<string, any>> ( id: MaybeRef<string> ): Promise<_AsyncData<AdjacentSiblings<T> | null, FetchError<any> | null>> {
    let url_ = "/api/content/{id}/siblings/adjacent";

    const idUnref = unref( id );
    if ( idUnref === undefined || idUnref === null ) {
      throw new Error( "The parameter 'id' must be defined." );
    }
    url_ = url_.replace( "{id}", encodeURIComponent( "" + idUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: UseApiFetchOptions<AdjacentSiblings<T>> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<AdjacentSiblings<T>>( url_, options_ );
  }

  function getChildren<T = Record<string, any>> ( idOrPath: MaybeRef<string>, options?: Omit<UseQueryOptions<T>, "fetch"> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    const idOrPathRef = toRef( idOrPath );
    return getItemsFromQuery<T>( {
      fetch: computed( () => `children:${idOrPathRef.value}` ),
      ...options,
    } );
  }

  function getAncestors<T = Record<string, any>> ( idOrPath: MaybeRef<string>, options?: Omit<UseQueryOptions<T>, "fetch"> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    const idOrPathRef = toRef( idOrPath );
    return getItemsFromQuery<T>( {
      fetch: computed( () => `ancestors:${idOrPathRef.value}` ),
      sort: ["level:desc"],
      ...options,
    } );
  }

  function getDescendants<T = Record<string, any>> ( idOrPath: MaybeRef<string>, options?: Omit<UseQueryOptions<T>, "fetch"> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    const idOrPathRef = toRef( idOrPath );
    return getItemsFromQuery<T>( {
      fetch: computed( () => `descendants:${idOrPathRef.value}` ),
      ...options,
    } );
  }

  function getByType<T = Record<string, any>> ( type: MaybeRef<string>, options?: Omit<UseQueryOptions<T>, "filter"> ): Promise<_AsyncData<ContentItems<T> | null, FetchError<any> | null>> {
    const typeRef = toRef( type );
    return getItemsFromQuery<T>( {
      filter: {
        contentType: computed( () => typeRef.value ),
      },
      ...options,
    } );
  }

  return {
    getById,
    getByRoute,
    getItemsById,
    getItemsFromQuery,
    getByTypeSingle,
    getSiblings,
    getAdjacentSiblings,
    getChildren,
    getAncestors,
    getDescendants,
    getByType,
  };
}
